import React, { useState } from 'react'
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { errorLogApi, ErrorLogItem, ErrorLogDetail, ErrorLogUpdateBody } from '../api/errorLog'
import JsonDetailDialog from '../components/common/JsonDetailDialog'

const GRAVIDADE_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critico: 'error',
  alto: 'error',
  medio: 'warning',
  baixo: 'info',
}

const compactFieldSx = {
  '& .MuiInputBase-root': { borderRadius: 1.5, minHeight: 28 },
  '& .MuiInputBase-input': { py: 0.5 },
  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
}

const formatDateTimeForAPI = (dateTimeLocal: string): string | null => {
  if (!dateTimeLocal) return null
  return new Date(dateTimeLocal).toISOString()
}

const ErrorLogPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [ambiente, setAmbiente] = useState<string>('')
  const [gravidade, setGravidade] = useState<string>('')
  const [corrigido, setCorrigido] = useState<boolean | ''>('')
  const [since, setSince] = useState<string>('')
  const [until, setUntil] = useState<string>('')
  const [skip, setSkip] = useState(0)
  const limit = 50
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; content?: string | null; title?: string }>({
    open: false,
    content: '',
    title: 'Detalhes do erro',
  })
  const [editDialog, setEditDialog] = useState<{ open: boolean; item: ErrorLogItem | null }>({
    open: false,
    item: null,
  })
  const [editProvidencia, setEditProvidencia] = useState('')
  const [editCorrigido, setEditCorrigido] = useState(false)
  const [editGravidade, setEditGravidade] = useState('')

  const { data: list = [], isLoading, refetch } = useQuery({
    queryKey: [
      'error-log',
      { ambiente: ambiente || null, gravidade: gravidade || null, corrigido, since, until, skip, limit },
    ],
    queryFn: () =>
      errorLogApi.list({
        ambiente: ambiente || null,
        gravidade: gravidade || null,
        corrigido: corrigido === '' ? undefined : !!corrigido,
        since: formatDateTimeForAPI(since) || null,
        until: formatDateTimeForAPI(until) || null,
        skip,
        limit,
      }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ErrorLogUpdateBody }) => errorLogApi.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['error-log'] })
      setEditDialog({ open: false, item: null })
    },
  })

  const openDetail = (item: ErrorLogItem) => {
    errorLogApi.getById(item.id).then((full: ErrorLogDetail) => {
      setDetailDialog({
        open: true,
        title: `Erro #${item.id} — ${item.onde || 'N/A'}`,
        content: full.details ? JSON.stringify(full.details, null, 2) : null,
      })
    })
  }

  const openEdit = (item: ErrorLogItem) => {
    setEditProvidencia(item.providencia_tomada ?? '')
    setEditCorrigido(!!item.corrigido)
    setEditGravidade(item.gravidade)
    setEditDialog({ open: true, item })
  }

  const handleSaveEdit = () => {
    const item = editDialog.item
    if (!item) return
    updateMutation.mutate({
      id: item.id,
      body: {
        providencia_tomada: editProvidencia || null,
        corrigido: editCorrigido,
        gravidade: editGravidade,
      },
    })
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }} display="flex" flexDirection="column" gap={0}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container columnSpacing={1.5} rowSpacing={1.5}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Ambiente</InputLabel>
              <Select value={ambiente} label="Ambiente" onChange={(e) => setAmbiente(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="dev">dev</MenuItem>
                <MenuItem value="teste">teste</MenuItem>
                <MenuItem value="producao">producao</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Gravidade</InputLabel>
              <Select value={gravidade} label="Gravidade" onChange={(e) => setGravidade(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="critico">Crítico</MenuItem>
                <MenuItem value="alto">Alto</MenuItem>
                <MenuItem value="medio">Médio</MenuItem>
                <MenuItem value="baixo">Baixo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Corrigido</InputLabel>
              <Select
                value={corrigido === '' ? '' : corrigido ? 'true' : 'false'}
                label="Corrigido"
                onChange={(e) => setCorrigido(e.target.value === '' ? '' : e.target.value === 'true')}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="false">Não</MenuItem>
                <MenuItem value="true">Sim</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Data inicial"
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Data final"
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2} display="flex" alignItems="center" gap={1}>
            <Button variant="contained" size="small" onClick={() => refetch()} disabled={isLoading} sx={{ minHeight: 28 }}>
              Filtrar
            </Button>
            {skip > 0 && (
              <Button size="small" onClick={() => setSkip((s) => Math.max(0, s - limit))}>
                Anterior
              </Button>
            )}
            {list.length === limit && (
              <Button size="small" onClick={() => setSkip((s) => s + limit)}>
                Próxima
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Onde</TableCell>
              <TableCell>Ambiente</TableCell>
              <TableCell>Gravidade</TableCell>
              <TableCell>Corrigido</TableCell>
              <TableCell>Providência</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.id}</TableCell>
                <TableCell>
                  {row.created_at
                    ? new Date(row.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                    : '-'}
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.onde ?? '-'}
                </TableCell>
                <TableCell>{row.ambiente ?? '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={row.gravidade}
                    size="small"
                    color={GRAVIDADE_COLOR[row.gravidade] ?? 'default'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>{row.corrigido ? 'Sim' : 'Não'}</TableCell>
                <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.providencia_tomada ? row.providencia_tomada.slice(0, 40) + (row.providencia_tomada.length > 40 ? '…' : '') : '-'}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openDetail(row)}>
                    Ver
                  </Button>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(!list || list.length === 0) && !isLoading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Nenhum registro
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <JsonDetailDialog
        open={detailDialog.open}
        title={detailDialog.title}
        rawContent={detailDialog.content}
        onClose={() => setDetailDialog({ open: false, content: '', title: 'Detalhes do erro' })}
      />

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Tratativa — Erro #{editDialog.item?.id}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Providência tomada"
                value={editProvidencia}
                onChange={(e) => setEditProvidencia(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={editCorrigido} onChange={(e) => setEditCorrigido(e.target.checked)} />}
                label="Corrigido"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Gravidade</InputLabel>
                <Select value={editGravidade} label="Gravidade" onChange={(e) => setEditGravidade(e.target.value)}>
                  <MenuItem value="critico">Crítico</MenuItem>
                  <MenuItem value="alto">Alto</MenuItem>
                  <MenuItem value="medio">Médio</MenuItem>
                  <MenuItem value="baixo">Baixo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, item: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ErrorLogPage
